import React from 'react';
import { useTranslation } from 'react-i18next';

import { LocalizedDateInput } from '../../../shared/components';
import type { CalendarRecurrence, DayOfWeek, RecurringFrequency } from '../types/Calendar';
import { buildDefaultWeeklyRecurrence } from '../utils/calendarRecurrenceUtils';
import styles from './CalendarPage.module.css';

const WEEKDAY_OPTIONS: DayOfWeek[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

type CalendarEventRecurrenceFieldsProps = {
  value: CalendarRecurrence;
  onChange: (value: CalendarRecurrence) => void;
  eventStartLocal: string;
  showApplyToSeries?: boolean;
  applyToSeries?: boolean;
  onApplyToSeriesChange?: (value: boolean) => void;
};

export const CalendarEventRecurrenceFields: React.FC<CalendarEventRecurrenceFieldsProps> = ({
  value,
  onChange,
  eventStartLocal,
  showApplyToSeries = false,
  applyToSeries = false,
  onApplyToSeriesChange,
}) => {
  const { t } = useTranslation('calendar');

  const update = React.useCallback(<K extends keyof CalendarRecurrence>(key: K, next: CalendarRecurrence[K]) => {
    onChange({ ...value, [key]: next });
  }, [onChange, value]);

  const handleFrequencyChange = (frequency: RecurringFrequency) => {
    if (frequency === 'none') {
      onChange({ ...value, frequency, daysOfWeek: [] });
      return;
    }

    if (frequency === 'weekly' && value.daysOfWeek.length === 0) {
      const startAt = eventStartLocal ? new Date(eventStartLocal) : new Date();
      onChange(buildDefaultWeeklyRecurrence(Number.isNaN(startAt.getTime()) ? new Date() : startAt));
      return;
    }

    onChange({ ...value, frequency });
  };

  const toggleWeekday = (day: DayOfWeek) => {
    const exists = value.daysOfWeek.includes(day);
    const daysOfWeek = exists
      ? value.daysOfWeek.filter((item) => item !== day)
      : [...value.daysOfWeek, day];

    onChange({ ...value, daysOfWeek });
  };

  return (
    <div
      className={[
        styles.recurrenceSection,
        value.frequency !== 'none' ? styles.recurrenceSectionActive : '',
      ].filter(Boolean).join(' ')}
    >
      <div className={styles.recurrenceSectionHeader}>
        <div className={styles.recurrenceSectionIntro}>
          <span className={styles.recurrenceSectionEyebrow}>
            {t('recurrenceGroup', { defaultValue: 'Schedule repeat' })}
          </span>
          <h4 className={styles.recurrenceSectionTitle}>
            {t('recurrence', { defaultValue: 'Recurrence' })}
          </h4>
          <p className={styles.recurrenceSectionHint}>
            {value.frequency === 'none'
              ? t('recurrenceHintOff', { defaultValue: 'Choose a repeat pattern if this event should recur.' })
              : t('recurrenceHintOn', { defaultValue: 'Configure how often this event repeats.' })}
          </p>
        </div>
      </div>

      <div className={styles.recurrenceFields}>
      <div className="form-row">
        <label>{t('recurrenceFrequency', { defaultValue: 'Pattern' })}</label>
        <select
          className={`text-input ${styles.eventFormControl}`}
          value={value.frequency}
          onChange={(event) => handleFrequencyChange(event.target.value as RecurringFrequency)}
        >
          <option value="none">{t('recurrenceNone', { defaultValue: 'Does not repeat' })}</option>
          <option value="daily">{t('recurrenceDaily', { defaultValue: 'Daily' })}</option>
          <option value="weekly">{t('recurrenceWeekly', { defaultValue: 'Weekly' })}</option>
          <option value="monthly">{t('recurrenceMonthly', { defaultValue: 'Monthly' })}</option>
          <option value="yearly">{t('recurrenceYearly', { defaultValue: 'Yearly' })}</option>
        </select>
      </div>

      {value.frequency !== 'none' ? (
        <>
          <div className="form-row">
            <label>{t('recurrenceInterval', { defaultValue: 'Repeat every' })}</label>
            <div className={styles.recurrenceIntervalRow}>
              <input
                className={`text-input ${styles.eventFormControl}`}
                type="number"
                min={1}
                max={52}
                value={value.interval}
                onChange={(event) => update('interval', Math.max(1, Number(event.target.value) || 1))}
              />
              <span className={styles.recurrenceIntervalSuffix}>
                {t(`recurrenceIntervalUnit.${value.frequency}`, { defaultValue: value.frequency, count: value.interval })}
              </span>
            </div>
          </div>

          {value.frequency === 'weekly' ? (
            <div className="form-row">
              <label>{t('recurrenceWeekdays', { defaultValue: 'On days' })}</label>
              <div className={styles.recurrenceWeekdayRow}>
                {WEEKDAY_OPTIONS.map((day) => {
                  const selected = value.daysOfWeek.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      className={`${styles.recurrenceWeekdayChip} ${selected ? styles.recurrenceWeekdayChipSelected : ''}`}
                      onClick={() => toggleWeekday(day)}
                    >
                      {t(`weekdaysShort.${day}`, { defaultValue: day.slice(0, 3) })}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {value.frequency === 'monthly' ? (
            <div className="form-row">
              <label>{t('recurrenceDayOfMonth', { defaultValue: 'Day of month' })}</label>
              <input
                className={`text-input ${styles.eventFormControl}`}
                type="number"
                min={1}
                max={31}
                value={value.dayOfMonth ?? ''}
                onChange={(event) => update('dayOfMonth', event.target.value ? Number(event.target.value) : undefined)}
              />
            </div>
          ) : null}

          <div className="form-row">
            <label htmlFor="recurrence-end-date">{t('recurrenceEndDate', { defaultValue: 'End date (optional)' })}</label>
            <LocalizedDateInput
              id="recurrence-end-date"
              className={styles.eventFormControl}
              value={value.endDate || ''}
              onValueChange={(nextValue) => update('endDate', nextValue || undefined)}
              emptyLabel={t('recurrenceEndDateEmpty', { defaultValue: 'No end date' })}
              aria-label={t('recurrenceEndDate', { defaultValue: 'End date (optional)' })}
              fillWidth
            />
          </div>

          <div className="form-row">
            <label>{t('recurrenceMaxOccurrences', { defaultValue: 'Max occurrences (optional)' })}</label>
            <input
              className={`text-input ${styles.eventFormControl}`}
              type="number"
              min={1}
              max={366}
              value={value.maxOccurrences ?? ''}
              onChange={(event) => update('maxOccurrences', event.target.value ? Number(event.target.value) : undefined)}
            />
          </div>
        </>
      ) : null}

      {showApplyToSeries ? (
        <div className={`form-row ${styles.eventFormCheckboxRow}`}>
          <label className={styles.eventFormCheckboxLabel}>
            <input
              type="checkbox"
              checked={applyToSeries}
              onChange={(event) => onApplyToSeriesChange?.(event.target.checked)}
            />
            {' '}{t('applyToSeries', { defaultValue: 'Apply changes to entire series' })}
          </label>
        </div>
      ) : null}
      </div>
    </div>
  );
};
